import { DialogueLine } from 'src/api/membot/types';
import { getMessageError, initalMessage } from 'src/apps/Conversations/utils';
import { ROMAN_INPUT_SCRIPT_LANGUAGES } from 'src/apps/Conversations/constants';
import { CommunicateEvent, CommunicateEventType, CommunicateState, WebViewClient } from './types';

export const initialState: CommunicateState = {
  goalReached: null,
  arePageDataLoaded: false,
  conversationStarted: false,
  conversationCompleted: false,
  disabled: false,
  missionWithPrompt: null,
  initialMessagesNumber: 0,
  message: '',
  messageError: getMessageError(initalMessage),
  dialogList: [],
  micEnabled: false,
  micStatus: 'idle',
  showTranslateHint: false,
  showResponseHints: false,
  loadingHints: false,
  hintLines: [],
  hintAnimationStatus: 'unset',
  negativeBotResponseSource: null,
  negativeBotResponseTarget: null,
  languagesInfo: {
    targetLanguageLocale: '',
    targetLanguageName: '',
    targetLanguageId: '',
    sourceLanguageLocale: '',
    sourceLanguageName: '',
    sourceLanguageId: '',
  },
  languagePairId: 0,
  locationSlug: '',
  client: WebViewClient.WEB,
  errorNotification: null,
  fatalError: null,
  audios: [],
  sessionId: '',
};
const undoLastMessage = (dialogList: DialogueLine[], targetLanguageLocale: string) => {
  // Remove the last two dialog lines: the user message and the bot response
  const [, lastDialogLine, ...reversedLastDialogHead] = dialogList.slice().reverse();
  let newLine;
  if (lastDialogLine.textTgtRoman && ROMAN_INPUT_SCRIPT_LANGUAGES.includes(targetLanguageLocale)) {
    newLine = {
      message: lastDialogLine.textTgtRoman,
      messageError: getMessageError(lastDialogLine.textTgtRoman),
    };
  } else if (lastDialogLine.textTgt) {
    newLine = {
      message: lastDialogLine.textTgt,
      messageError: getMessageError(lastDialogLine.textTgt),
    };
  } else {
    newLine = {};
  }
  return {
    dialogList: [...reversedLastDialogHead.slice().reverse()],
    ...newLine,
  };
};

export const reducer = (state: CommunicateState, event: CommunicateEvent): CommunicateState => {
  switch (event.type) {
    case CommunicateEventType.PageDataLoaded:
      return { ...state, arePageDataLoaded: true };
    case CommunicateEventType.UpdateDialogList:
      return { ...state, dialogList: event.payload };
    case CommunicateEventType.SetGoalReached:
      return { ...state, goalReached: event.payload };
    case CommunicateEventType.SetDisabled:
      return { ...state, disabled: event.payload };
    case CommunicateEventType.SetMission:
      return {
        ...state,
        missionWithPrompt: event.payload,
      };
    case CommunicateEventType.SetInitialMessagesNumber:
      return {
        ...state,
        initialMessagesNumber: event.payload,
      };
    case CommunicateEventType.SetMessage:
      return {
        ...state,
        message: typeof event.payload === 'string' ? event.payload : event.payload(state.message),
        messageError:
          typeof event.payload === 'string'
            ? getMessageError(event.payload, event.intl)
            : getMessageError(event.payload(state.message), event.intl),
        hintAnimationStatus: 'scheduling',
      };
    case CommunicateEventType.ToggleTranslateHint:
      return { ...state, showTranslateHint: !state.showTranslateHint };
    case CommunicateEventType.SetNegativeBotResponseSource:
      return { ...state, negativeBotResponseSource: event.payload };
    case CommunicateEventType.SetNegativeBotResponseTarget:
      return { ...state, negativeBotResponseTarget: event.payload };
    case CommunicateEventType.UndoLastMessage:
      if (state.goalReached?.goalReached || state.goalReached?.messageLimitReached) return state;

      return {
        ...state,
        ...undoLastMessage(state.dialogList, state.languagesInfo.targetLanguageLocale),
        micStatus: state.micStatus === 'listening' ? 'stopping' : state.micStatus,
      };
    case CommunicateEventType.SetShowResponseHints:
      return {
        ...state,
        showResponseHints: event.payload,
        hintAnimationStatus: event.payload ? 'inactive' : 'scheduling',
      };
    case CommunicateEventType.SetConversationStarted:
      return { ...state, conversationStarted: event.payload };
    case CommunicateEventType.SetConversationCompleted:
      return { ...state, conversationCompleted: event.payload };
    case CommunicateEventType.SetMicEnabled:
      return { ...state, micEnabled: event.payload };
    case CommunicateEventType.SetMicStatus:
      return {
        ...state,
        micStatus: event.payload,
        hintAnimationStatus: event.payload === 'idle' ? 'scheduling' : 'inactive',
      };
    case CommunicateEventType.SetLoadingHints:
      return {
        ...state,
        loadingHints: event.payload,
        hintAnimationStatus: 'inactive',
      };
    case CommunicateEventType.SetHintLines:
      return { ...state, hintLines: event.payload };
    case CommunicateEventType.SetHintAnimationStatus:
      return { ...state, hintAnimationStatus: event.payload };
    case CommunicateEventType.SetErrorNotification:
      return { ...state, errorNotification: event.payload };
    case CommunicateEventType.SetFatalError:
      return { ...state, fatalError: event.payload };
    case CommunicateEventType.SetLanguagesInfo:
      return { ...state, languagesInfo: event.payload };
    case CommunicateEventType.UpdateAudios: {
      const currentAudioIndex = state.audios?.findIndex(
        audio => audio.audioCurrent.src === event.payload.audioCurrent.src,
      );
      if (currentAudioIndex === -1) {
        return { ...state, audios: [...state.audios, event.payload] };
      } else {
        const updatedAudios = state.audios;
        updatedAudios[currentAudioIndex] = event.payload;

        return { ...state, audios: updatedAudios };
      }
    }
  }
};
